import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import moment from 'moment';
import GreatDatePicker from '../../../ui/forms/fields/datePicker';
import Select from '../../../ui/forms/fields/select';
import { MenuItem, useTheme } from '@mui/material';
import PrimaryButton from '../../../ui/buttons/primaryButton';
import {
  getReport,
  getProgressReport,
  getInceptionDate
} from '../../../../features/reports/report.slice';
import CircularProgress from '@mui/material/CircularProgress';

const schema = yup.object().shape({
  startDate: yup
    .string()
    .nullable()
    .transform((curr, orig) =>
      orig instanceof Date || orig?._isAMomentObject ? orig?.format('YYYY-MM-DD') : orig
    )
    .when('type', {
      is: (value) => value !== 'progress',
      then: (schema) => schema.required('Start date is required. Please select a date.')
    }),

  endDate: yup
    .string()
    .nullable()
    .transform((curr, orig) =>
      orig instanceof Date || orig?._isAMomentObject ? orig?.format('YYYY-MM-DD') : orig
    )
    .when('type', {
      is: (value) => value !== 'progress',
      then: (schema) => schema.required('End date is required. Please select a date.')
    }),

  type: yup.string().required('Please select a report type from the dropdown.')
});

function Reports() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.report.loading);
  const inceptionDate = useSelector((state) => state.report.inceptionDate);
  const user = useSelector((state) => state.auth.user);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    trigger
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      type: '',
      startDate: null,
      endDate: null
    }
  });

  const selectedType = useWatch({ control, name: 'type' });

  useEffect(() => {
    if (selectedType) {
      dispatch(getInceptionDate(selectedType));
    }
  }, [selectedType, dispatch]);

  useEffect(() => {
    if (inceptionDate) {
      const inception = moment(inceptionDate).startOf('day');
      const start = moment(getValues('startDate'));
      const end = moment(getValues('endDate'));

      if (start.isValid() && start.isBefore(inception)) {
        setValue('startDate', null);
      }

      if (end.isValid() && end.isBefore(inception)) {
        setValue('endDate', null);
      }
    }
  }, [inceptionDate, setValue, getValues, trigger]);

  const onSubmit = (data) => {
    if (data.type === 'progress') {
      dispatch(getProgressReport({ startDate: data.startDate, endDate: data.endDate }));
    } else {
      dispatch(getReport(JSON.stringify(data)));
    }
  };

  const minDate = inceptionDate ? moment(inceptionDate).format('YYYY-MM-DD') : undefined;

  return (
    <div
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          padding: 'auto',
          maxWidth: '700px'
        }}>
        <Select
          name="type"
          label="Report"
          control={control}
          error={errors?.type}
          helperText={errors?.type?.message}
          sx={{
            marginRight: theme.spacing(2),
            width: '25%'
          }}>
          <MenuItem value={'progress'}>Progress Report</MenuItem>
          <MenuItem value={'ratings'}>Rating</MenuItem>
          <MenuItem value={'savedApplications'}>Saved Applications</MenuItem>
          <MenuItem value={'speakToAConsultant'}>Speak To Consultant</MenuItem>
          <MenuItem value={'submissions'}>Submissions</MenuItem>
        </Select>

        <GreatDatePicker
          label="Start Date"
          name="startDate"
          control={control}
          error={errors?.startDate}
          minDate={minDate}
          sx={{
            marginRight: theme.spacing(2),
            width: '25%'
          }}
        />

        <GreatDatePicker
          label="End Date"
          name="endDate"
          control={control}
          error={errors?.endDate}
          minDate={minDate}
          sx={{
            marginRight: theme.spacing(2),
            width: '25%'
          }}
        />

        <PrimaryButton
          type="submit"
          sx={{
            marginTop: theme.spacing(1)
          }}>
          Download
          {loading && <CircularProgress size={24} sx={{ ml: 2, color: '#CC6323' }} />}
        </PrimaryButton>
      </form>
    </div>
  );
}

export default Reports;
